// /utils/printExpPreview.js

import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Format date values to DD/MM/YYYY
 * @param {string|Date} dateValue - Date value to format
 * @returns {string} - Formatted date string or empty string if invalid
 */
function formatDate(dateValue) {
  if (!dateValue) return '';
  
  try {
    const date = new Date(dateValue);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '';
    
    // Format as DD/MM/YYYY
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error('Date formatting error:', error);
    return '';
  }
}

/**
 * Format a variable value to display nicely (handling null, undefined, etc.)
 * @param {any} value - The variable value to format
 * @returns {string} - Formatted value as string
 */
function formatVariableValue(value) {
  // Return empty string for null or undefined
  if (value === null || value === undefined) {
    return '';
  }
  
  // Convert to string
  const strValue = String(value);
  
  // Return empty string for "null" or "undefined" literal strings or empty strings
  if (strValue === 'null' || strValue === 'undefined' || strValue.trim() === '') {
    return '';
  }
  
  return strValue;
}

/**
 * Process template text and replace variables and conditions
 * @param {string} text - Template text with variables and conditions
 * @param {Object} variables - The variables object for substitution
 * @returns {string} - Processed text with variables replaced and conditions evaluated
 */
// Fix for printTemplate.js
// Replace the processTemplateText function with this version:

function processTemplateText(text, variables) {
    if (!text) return '';
    
    // First handle null and !null conditional logic patterns
    let processedText = text.replace(/\{\{if:([^:]+?)(null|!null):([^}]+)\}\}/g, 
      (match, variable, operator, result) => {
        const trimmedVariable = variable.trim();
        const trimmedOperator = operator.trim();
        const trimmedResult = result.trim();
        
        // Get the actual variable value
        const actualValue = variables[trimmedVariable] !== undefined 
          ? formatVariableValue(variables[trimmedVariable]) 
          : '';
        
        // Determine if condition is satisfied
        let conditionMet = false;
        
        // Handle special null check cases
        if (trimmedOperator === 'null') {
          // Check if the value is null, undefined, empty string, or just whitespace
          conditionMet = !actualValue || actualValue.trim() === '';
        } else if (trimmedOperator === '!null') {
          // Check if the value exists and is not just whitespace
          conditionMet = actualValue && actualValue.trim() !== '';
        }
        
        return conditionMet ? trimmedResult : '';
      });
    
    // Then handle regular conditional logic with operators
    processedText = processedText.replace(/\{\{if:([^:=!<>]+)([=!<>]+)([^:]*):([^}]+)\}\}/g, 
      (match, variable, operator, value, result) => {
        const trimmedVariable = variable.trim();
        const trimmedOperator = operator.trim();
        const trimmedValue = value ? value.trim() : '';
        const trimmedResult = result.trim();
        
        // Get the actual variable value
        const actualValue = variables[trimmedVariable] !== undefined 
          ? formatVariableValue(variables[trimmedVariable]) 
          : '';
        
        // Determine if condition is satisfied
        let conditionMet = false;
        
        // Handle regular comparison operators
        switch (trimmedOperator) {
          case '=':
          case '==':
            conditionMet = actualValue === trimmedValue;
            break;
          case '!=':
            conditionMet = actualValue !== trimmedValue;
            break;
          case '>':
            conditionMet = Number(actualValue) > Number(trimmedValue);
            break;
          case '<':
            conditionMet = Number(actualValue) < Number(trimmedValue);
            break;
          case '>=':
            conditionMet = Number(actualValue) >= Number(trimmedValue);
            break;
          case '<=':
            conditionMet = Number(actualValue) <= Number(trimmedValue);
            break;
          default:
            conditionMet = actualValue === trimmedValue;
        }
        
        return conditionMet ? trimmedResult : '';
      });
    
    // Finally handle regular variable placeholders
    processedText = processedText.replace(/\{\{([^}]+)\}\}/g, (match, varName) => {
      const trimmedName = varName.trim();
      
      if (variables[trimmedName] === undefined) {
        return ''; // Replace with empty string if variable not found
      }
      
      // Return the formatted variable value (handling null/undefined)
      return formatVariableValue(variables[trimmedName]);
    });
    
    return processedText;
  }
  
  // Fix for printExpPreview.js
  // Replace the processTemplateText function with the exact same implementation as above.
  // The implementation is identical for both files.

/**
 * Generates a preview HTML of the template with variables substituted
 * @param {Array} templateJSON - The template JSON array with element definitions
 * @param {Object} variablesJSON - The variables object for substitution
 * @returns {string} - The HTML content
 */
function generatePreviewHTML(templateJSON, variablesJSON) {
  // Make a deep copy to avoid modifying the original object
  const processedVariables = JSON.parse(JSON.stringify(variablesJSON));
  
  // Clean up all null values to ensure they're all empty strings, not "null" text
  Object.keys(processedVariables).forEach(key => {
    if (processedVariables[key] === null || processedVariables[key] === undefined) {
      processedVariables[key] = '';
    }
  });
  
  // Apply special logic: if ville_dest is not null, set ADRESSE and VILLE to null
  if (processedVariables.ville_dest && 
      String(processedVariables.ville_dest).trim() !== '') {
    // When ville_dest has a value, set ADRESSE and VILLE to empty strings
    processedVariables.ADRESSE = '';
    processedVariables.VILLE = '';
  }
  
  // Format date fields to DD/MM/YYYY
  const dateFields = [
    'date_exp', 'date_fac', 'date_reg', 'date_recep', 
    'date_dev', 'der_maj'
  ];
  
  dateFields.forEach(field => {
    if (processedVariables[field]) {
      processedVariables[field] = formatDate(processedVariables[field]);
    }
  });
  
  // Generate the HTML content
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Template Preview</title>
      <style>
        /* These settings force landscape mode */
        @page {
          size: A4 landscape;  /* Use A4 paper in landscape orientation */
          margin: 0;
        }
        
        html, body {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          font-family: Arial, sans-serif;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .canvas-container { 
          width: 100%;
          height: 100%;
          position: relative; 
          background-color: white;
        }
        
        .element {
          position: absolute;
          /* No borders in the printed version */
        }
        
        .element-text {
          font-weight: bold;
        }
        
        @media print {
          /* This reinforces landscape printing */
          @page {
            size: landscape;
          }
          
          body {
            width: 297mm;  /* A4 width in landscape */
            height: 210mm; /* A4 height in landscape */
          }
        }
      </style>
    </head>
    <body>
      <div class="canvas-container">
        ${templateJSON.map(element => `
          <div class="element" style="
            left: ${element.x}px; 
            top: ${element.y}px; 
            width: ${element.width}px; 
            height: ${element.height}px;
            color: ${element.textColor || '#000'};"
          >
            <div class="element-text" style="font-size: ${element.fontSize || 16}px;">
              ${processTemplateText(element.text || element.id, processedVariables)}
            </div>
          </div>
        `).join('')}
      </div>
    </body>
    </html>
  `;
  
  return htmlContent;
}

/**
 * Generates a PDF from HTML content
 * @param {string} htmlString - The HTML content to convert to PDF
 * @returns {Promise<Blob>} - A Promise that resolves to a PDF Blob
 */
async function generatePdfFromHTML(htmlString) {
  const container = document.createElement('div');
  container.style.width = '297mm'; // A4 width in landscape
  container.style.height = '210mm'; // A4 height in landscape
  container.style.position = 'absolute';
  container.style.left = '-10000px';
  container.innerHTML = htmlString;
  document.body.appendChild(container);
  
  try {
    const pdf = new jsPDF('l', 'mm', 'a4'); // 'l' for landscape
    
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      letterRendering: true,
      backgroundColor: '#FFFFFF'
    });
    
    const imgData = canvas.toDataURL('image/jpeg', 1.0);
    pdf.addImage(imgData, 'JPEG', 0, 0, 297, 210); // A4 dimensions in landscape
    
    return pdf.output('blob');
  } finally {
    document.body.removeChild(container);
  }
}

/**
 * Previews a template with data and returns a URL to the PDF
 * @param {Array} templateJSON - The template JSON array with element definitions
 * @param {Object} variablesJSON - The variables object for substitution
 * @returns {Promise<string>} - A Promise that resolves to a URL for the preview PDF
 */
async function previewTemplate(templateJSON, variablesJSON) {
  try {
    // Generate HTML content with template and variables
    const htmlContent = generatePreviewHTML(templateJSON, variablesJSON);
    
    // Generate PDF from HTML
    const pdfBlob = await generatePdfFromHTML(htmlContent);
    
    // Create a URL for the PDF blob
    const pdfUrl = URL.createObjectURL(pdfBlob);
    
    return pdfUrl;
  } catch (error) {
    console.error('Error generating preview:', error);
    throw new Error(`Failed to generate preview: ${error.message}`);
  }
}

export default previewTemplate;